import { useEffect, useState, type ReactNode } from "react";
import { NoticesResponseSchema, type Notice } from "@legacy-manager/shared";

import { useIdioma } from "../i18n";
import "./aviso-do-servidor.css";

/**
 * A faixa que a equipe publica pelo painel: manutenção, incidente, novidade.
 *
 * Lida sem sessão (`GET /notices`), já no idioma do jogador, a cada dez
 * minutos. Fechar um aviso vale para a sessão do navegador: ele não volta até
 * o jogo ser aberto de novo, e um aviso novo aparece por cima do fechado.
 */

const INTERVALO_MS = 10 * 60 * 1000;
const CHAVE_DOS_FECHADOS = "avisos-fechados";

function enderecoDaApi(): string {
  const configurado = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");
  if (configurado) return configurado;
  return typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;
}

function lerFechados(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(CHAVE_DOS_FECHADOS) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function guardarFechados(fechados: Set<string>): void {
  try {
    sessionStorage.setItem(CHAVE_DOS_FECHADOS, JSON.stringify([...fechados]));
  } catch {
    // Sem armazenamento (aba anônima antiga, cota cheia): o aviso volta na próxima leitura, e é só isso.
  }
}

export function AvisoDoServidor(): ReactNode {
  const { idioma } = useIdioma();
  const [avisos, definirAvisos] = useState<Notice[]>([]);
  const [fechados, definirFechados] = useState<Set<string>>(lerFechados);

  useEffect(() => {
    let vivo = true;
    const ler = async (): Promise<void> => {
      try {
        const resposta = await fetch(`${enderecoDaApi()}/v1/notices?locale=${encodeURIComponent(idioma)}`);
        if (!resposta.ok) return;
        const corpo = NoticesResponseSchema.parse(await resposta.json());
        if (vivo) definirAvisos(corpo.items);
      } catch {
        // Sem rede ou servidor fora: a faixa simplesmente não aparece.
      }
    };
    void ler();
    const relogio = setInterval(() => void ler(), INTERVALO_MS);
    return () => {
      vivo = false;
      clearInterval(relogio);
    };
  }, [idioma]);

  const visivel = avisos.find((aviso) => !fechados.has(aviso.id));
  if (!visivel) return null;

  const fechar = (): void => {
    const novos = new Set(fechados);
    novos.add(visivel.id);
    definirFechados(novos);
    guardarFechados(novos);
  };

  return (
    <div className="aviso-do-servidor" data-nivel={visivel.level} role="status">
      <div className="aviso-do-servidor-texto">
        <strong>{visivel.title}</strong>
        <span>{visivel.body}</span>
      </div>
      <button type="button" className="ghost aviso-do-servidor-fechar" onClick={fechar} aria-label="Fechar">
        ×
      </button>
    </div>
  );
}
